import saveAs from 'file-saver';
import { StatusCodes } from 'http-status-codes';
import moment from 'moment';
import { useState, useEffect } from 'react';
import { PuffLoader } from 'react-spinners';
import styled from 'styled-components';
import AutocompleteComponent from '~/src/components/common/forms/Autcomplete/AutocompleteComponent';
import TableWrapper from '~/src/components/common/forms/TableWrapper/TableWrapper';

import TitleComponent from '~/src/components/common/forms/Title/TitleComponent';
import { DateRangeFilterCell } from '~/src/components/common/grid/DateRangeFilterCell';
import WhitePad from '~/src/components/common/WhitePad/WhitePad';
import InventoryApi from '~/src/services/api/InventoryApi';
import { API_URL, DATE_FORMAT, DATE_FORMAT_TIME, maxReportDate, minReportDate } from '~/src/utils/constants';
import uplaod from '../../../../static/img/upload.svg';
import PrimaryButton from '~/src/components/common/forms/Button/PrimaryButton';
import { Link, useLocation } from 'react-router-dom';
import { ReportRequestDto } from '~/src/types/ReportRequestDto';

const StyledLink = styled(Link)`
  color: black;
  text-decoration: none;

  &:hover {
    color: black;
    text-decoration: none;
    cursor: pointer;
  }
`;

export default function InventoryReportsIncoming(): JSX.Element {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const warehouseId = queryParams.get('warehouseId') || '';
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [resData, setResData] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const fetch = async (): Promise<void> => {
    setIsSubmitting(true);

    const requestDto: ReportRequestDto = {
      fromDate: moment(fromDate).format('yyyy-MM-DD'),
      toDate: moment(toDate).format('yyyy-MM-DD'),
      productId,
      warehouseId,
    };

    const result = await InventoryApi.reportIncoming(requestDto);
    if (result?.status === StatusCodes.OK) {
      setResData(result.data);
    }
    setIsSubmitting(false);
  };

  const fetchExcel = async (): Promise<void> => {
    const requestDto: ReportRequestDto = {
      fromDate: moment(fromDate).format('yyyy-MM-DD'),
      toDate: moment(toDate).format('yyyy-MM-DD'),
      productId,
      warehouseId,
    };

    const result = await InventoryApi.reportIncomingExcel(requestDto);
    if (result?.status === StatusCodes.OK) {
      const courseSheet = result.data;
      if (courseSheet) {
        const blob = new Blob([courseSheet], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        saveAs(blob, `Warehouse-incoming-logs.xlsx`);
      }
    }
  };

  const handleDateChange = (event: any) => {
    if (event.value) {
      if (event.value.min) {
        setFromDate(moment(event.value.min).toDate());
      } else {
        setFromDate(null);
      }
      if (event.value.max) {
        setToDate(moment(event.value.max).toDate());
      } else {
        setToDate(null);
      }
    } else {
      setFromDate(null);
      setToDate(null);
      setResData([]);
    }
  };

  const handleProductChange = (filed: string, value: string) => {
    setProductId(value);
  };

  return (
    <>
      <WhitePad isFluid>
        <TitleComponent
          title="Inventory Incoming"
          iconRight={uplaod}
          onRightHandler={fetchExcel}
          titleRight="Download Excel"
        />

        <Controls>
          <DateRangeFilterCell
            onChange={handleDateChange}
            maxValueProp={toDate}
            minValueProp={fromDate}
            minSelectableDate={minReportDate}
            maxSelectableDate={maxReportDate}
          />
          <PrimaryButton
            title="Confirm"
            onClick={fetch}
            disabled={!fromDate || !toDate}
          />
          <AutocompleteComponent
            url={`${API_URL}/products/products/get-order-list`}
            name="Product"
            onChange={handleProductChange}
            getOptionLabel={(option) => `${option.name} ${option.gtinCu}`}
          />
        </Controls>
        {isSubmitting ? (
          <PuffLoader loading={true} size={25} />
        ) : (
          <Wrapper>
            <TableWrapper>
              <table>
                <thead>
                  <tr>
                    <ThCell>id</ThCell>
                    <ThCell>name</ThCell>
                    <ThCell>Manufacturer</ThCell>
                    <ThCell>Date</ThCell>
                    <ThCell>Hour</ThCell>
                    <ThCell>Lot</ThCell>
                    <ThCell>Unit</ThCell>
                    <ThCell>Quantity</ThCell>
                    <ThCell>Expiration Date</ThCell>
                    <ThCell>Purchase Order</ThCell>
                    <ThCell>Document</ThCell>
                    <ThCell>Storage Condition</ThCell>
                    <ThCell>Warehouse</ThCell>
                    <ThCell>Signature</ThCell>
                  </tr>
                </thead>
                <tbody>
                  {resData?.map((x, index) => (
                    <tr key={index}>
                      <TdCell>{index}</TdCell>
                      <TdCell>{x.productName}</TdCell>
                      <TdCell>{x.manufacturerName}</TdCell>
                      <TdCell>{moment(x.dateTime).format(DATE_FORMAT)}</TdCell>
                      <TdCell>{moment(x.dateTime).format(DATE_FORMAT_TIME)}</TdCell>
                      <TdCell>{x.lot}</TdCell>
                      <TdCell>{x.unit}</TdCell>
                      <TdCell>{x.quantity}</TdCell>
                      <TdCell>{moment(x.expirationDate).format(DATE_FORMAT)}</TdCell>
                      <TdCell>
                        <StyledLink to={`/purchase-orders/edit/${x?.purchaseOrder?.id}`} target="_blank" rel="noopener noreferrer">
                          {x?.purchaseOrder?.orderNumber}
                        </StyledLink>
                      </TdCell>
                      <TdCell>--</TdCell>
                      <TdCell>{x.storageCondition}</TdCell>
                      <TdCell>{x.warehouseName}</TdCell>
                      <TdCell>{x.personName}</TdCell>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrapper>
          </Wrapper>
        )}
      </WhitePad>
    </>
  );
}

const Wrapper = styled.div``;

const ThCell = styled.th``;
const TdCell = styled.td``;

const Controls = styled.div`
  display: flex;
  padding: 5px;
  align-items: center;
  gap: 10px;
`;